import React from "react";
import { Container } from "react-bootstrap";
import styled from "styled-components";
import { urlSafeElectorate } from "~/helpers/constants/Electorates";
import { PreferenceTas25SpecialClark12 } from "./PreferenceTas25SpecialClark12";

/**
 * Div holding the candidate name and optionally party name
 */
const CandidateContainer = styled.div`
  min-height: 40px;
  display: flex;
  align-items: center;
`;

/**
 * A div for the candidate name.
 */
const CandidateName = styled.div`
  min-height: 40px;
  display: flex;
  align-items: center;
`;

/**
 * A box for the voting square.
 */
const VotingSquareContainer = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: 40px;
  height: 40px;
`;

/**
 * The voting square itself.
 * May contain a preference or be empty.
 */
const VotingSquare = styled.div`
  border: 1px solid black;
  width: 40px;
  height: 40px;
  text-align: center;
  font-size: 24px;
  line-height: 40px;
  margin-left: auto;
  margin-right: 10px;
`;

type PreferenceProps = {
  /** Array of Greens parties names as they appear on the ballot for this election.
   * This is an array because for Federal elections,
   * different state parties may appear with differing names.
   */
  partyNameOnBallot: string[];
  /** The numerical preference for this entry on the ballot.
   * Can be blank for an "open ballot". */
  preference?: number;
  /** The name of the electorate.
   * Only used for generating links to candidate pages. */
  electorateName: string;
  /** The party name for this entry as it appears on the ballot.
   * Can be blank for an independent candidate in an election
   * where "independent" should not show beneath their name.
   */
  party?: string;
  /** The first name of this ballot entry as it appears on the ballot. */
  firstName: string;
  /** The last name of this ballot entry, as it appears on the ballot.  */
  lastName: string;
  /** Should the logo be shown for this candidate, regardless of whether they are a Greens candidate? */
  // showAllLogosOnBallot?: boolean;
  /** Should the first & last name be bolded if this entry is for the Greens candidate? */
  boldGreensCandidate?: boolean;
  /** Should the first & last name be linked to the candidate's webpage on greens.org.au
   * if this entry is for a Greens candidate?
   */
  linkCandidateOnBallot?: boolean;
};

/**
 * Represents a single entry on a ballot paper.
 */
const Preference: React.FC<PreferenceProps> = ({
  firstName,
  lastName,
  preference,
  party,
  electorateName,
  // showGreensLogoOnBallot,
  boldGreensCandidate,
  partyNameOnBallot,
  linkCandidateOnBallot,
}) => {
  const isGreensPreference = partyNameOnBallot.includes(party);
  let Candidate;
  if (isGreensPreference) {
    if (linkCandidateOnBallot) {
      Candidate = styled.a.attrs({
        href: `https://greens.org.au/candidates/${urlSafeElectorate(electorateName)}`,
        target: "_blank",
      })`
        ${boldGreensCandidate && "font-weight: bold;"};
      `;
    } else {
      Candidate = styled.span<PreferenceProps>`
        color: black;
        ${boldGreensCandidate && "font-weight: bold;"};
      `;
    }
  } else {
    Candidate = styled.span<PreferenceProps>`
      color: black;
      font-weight: normal;
    `;
  }
  const Row = styled.div`
    position: relative;
    padding-left: 50px;
    margin-bottom: 12px;
  `;


  if (electorateName === 'Clark') {
    if (preference === 1) {
      return (
         <PreferenceTas25SpecialClark12/>
      );
    } else if (preference === 2) {
      return;
    }
  }

  return (
    <Container>
      <Row>
        <VotingSquareContainer>
          {preference == 1 ? <VotingSquare style={{fontSize: 30}}><b>{preference}</b></VotingSquare> : <VotingSquare>{preference}</VotingSquare>}
        </VotingSquareContainer>
        <CandidateName>
          <Candidate boldGreensCandidate>
            <p style={{marginBottom: 0}}>{lastName}, {firstName}<br/>
            <small>{party}</small></p>
          </Candidate>
        </CandidateName>
      </Row>
    </Container>
  );
};

export default Preference;
